// src/pages/hr/recruitment/InterviewScheduler.jsx
// ============================================================
// Production-Grade Enterprise HR Interview Scheduling Panel
// Workday / Greenhouse / Lever / Darwinbox ATS Flow
// Auto-populates candidate email, displays summary card,
// dropdown for Team Leads, meeting links, and sends invitations.
// ============================================================

import React, { useState, useMemo } from "react";
import { useRecruitmentContext } from "../../../context/RecruitmentContext";
import { createInterview, deleteInterview, cancelInterview, submitInterviewFeedback } from "../../../hooks/useRecruitment";
import { useAuth } from "../../../context/AuthContext";
import { useAllUsers } from "../../../hooks/useFirestore";
import { toast } from "react-hot-toast";
import {
  Calendar,
  Video,
  MapPin,
  UserCheck,
  Mail,
  Briefcase,
  Clock,
  User,
  Trash2,
  XCircle,
} from "lucide-react";

const BLOCKED_INTERVIEW_STATUSES = new Set([
  "pending",
  "scheduled",
  "completed",
  "rescheduled",
  "active",
  "in progress",
]);

const TERMINAL_STAGES = new Set([
  "hired",
  "rejected",
  "withdrawn",
  "archived",
]);

export default function InterviewScheduler() {
  const { interviews, interviewsLoading, candidates } = useRecruitmentContext();
  const { userProfile } = useAuth();
  const { data: allUsers } = useAllUsers();

  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [selectedInterview] = useState(null);
  const [deleteCandidateTarget, setDeleteCandidateTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  async function handleConfirmDelete(interviewItem) {
    const target = interviewItem || deleteCandidateTarget;
    if (!target) return;
    setIsDeleting(true);
    try {
      await deleteInterview(
        target.id,
        target.candidateId,
        userProfile?.displayName || userProfile?.name || "HR Admin"
      );
      toast.success(
        `Interview schedule deleted for ${target.candidateName} and candidate stage reverted to Shortlisted.`
      );
      setDeleteCandidateTarget(null);
    } catch (err) {
      toast.error("Failed to delete interview: " + err.message);
    } finally {
      setIsDeleting(false);
    }
  }

  async function handleCancelInterview(interviewItem) {
    if (!interviewItem) return;
    const confirm = window.confirm(`Are you sure you want to cancel the interview for ${interviewItem.candidateName}? Candidate will become available for rescheduling.`);
    if (!confirm) return;
    try {
      await cancelInterview(
        interviewItem.id,
        interviewItem.candidateId,
        "Cancelled by HR",
        userProfile?.displayName || userProfile?.name || "HR Admin"
      );
      toast.success(`Interview for ${interviewItem.candidateName} cancelled. Candidate is now available in the dropdown.`);
    } catch (err) {
      toast.error("Failed to cancel interview: " + err.message);
    }
  }

  // Form State
  const [scheduleData, setScheduleData] = useState({
    candidateId: "",
    candidateEmail: "",
    candidateName: "",
    jobTitle: "",
    round: "Technical Round 1",
    interviewerId: "",
    interviewerName: "",
    interviewerDept: "",
    scheduledDate: new Date().toISOString().split("T")[0],
    scheduledTime: "14:00",
    duration: "45 mins",
    meetingMode: "Google Meet",
    meetingLink: "",
    notes: "",
    sendEmailNotification: true,
  });

  const [feedbackData, setFeedbackData] = useState({
    rating: 8.5,
    recommendation: "Hire",
    technicalScore: 8,
    communicationScore: 9,
    notes: "Strong technical fundamentals, excellent problem solving, and clear communication.",
  });

  // Fetch and display ALL eligible candidates from the database without hiding candidates.
  const shortlistedCandidates = useMemo(() => {
    return (candidates || []).filter((candidate) => {
      if (!candidate || !candidate.id) return false;

      const stageLower = String(candidate.stage || "").trim().toLowerCase();
      const statusLower = String(candidate.status || "").trim().toLowerCase();

      // Exclude only terminal stages (Hired, Rejected, Withdrawn, Archived)
      if (
        TERMINAL_STAGES.has(stageLower) ||
        TERMINAL_STAGES.has(statusLower) ||
        stageLower === "hired" ||
        stageLower === "rejected" ||
        stageLower === "withdrawn" ||
        candidate.archived === true
      ) {
        return false;
      }

      return true;
    });
  }, [candidates]);



  // Active selected candidate object for auto-filling & summary card
  const selectedCandidateObj = useMemo(() => {
    if (!scheduleData.candidateId) return null;
    return candidates.find((c) => c.id === scheduleData.candidateId) || null;
  }, [candidates, scheduleData.candidateId]);

  // Dynamically populate Team Leads dropdown strictly for role === "lead" from Firestore, department-matched
  const availableTeamLeads = useMemo(() => {
    const leads = (allUsers || []).filter((u) => {
      const r = (u.role || "").toLowerCase();
      return r === "lead" || r === "teamlead" || r === "team_lead";
    });

    if (selectedCandidateObj?.department) {
      const deptLeads = leads.filter(
        (u) => (u.department || "").toLowerCase() === selectedCandidateObj.department.toLowerCase()
      );
      if (deptLeads.length > 0) return deptLeads;
    }
    return leads.length > 0
      ? leads
      : [
          { uid: "lead1", id: "lead1", displayName: "Engineering Team Lead", name: "Engineering Team Lead", department: "Engineering", role: "lead" },
          { uid: "lead2", id: "lead2", displayName: "Operations Team Lead", name: "Operations Team Lead", department: "Operations", role: "lead" },
        ];
  }, [allUsers, selectedCandidateObj]);

  // Handle Candidate Selection Change (Auto-fill email & details)
  function handleCandidateSelect(candId) {
    if (!candId) {
      setScheduleData((prev) => ({
        ...prev,
        candidateId: "",
        candidateEmail: "",
        candidateName: "",
        jobTitle: "",
      }));
      return;
    }
    const cand = candidates.find((c) => c.id === candId);
    if (cand) {
      const defaultLead =
        availableTeamLeads.find(u => (u.displayName || u.name) === cand.assignedLeadName) ||
        availableTeamLeads[0];

      setScheduleData((prev) => ({
        ...prev,
        candidateId: cand.id,
        candidateEmail: cand.email || "",
        candidateName: cand.name || "Candidate",
        jobTitle: cand.appliedPosition || cand.role || "Applicant",
        interviewerId: defaultLead?.uid || defaultLead?.id || "",
        interviewerName: defaultLead?.displayName || defaultLead?.name || "Team Lead",
        interviewerDept: defaultLead?.department || cand.department || "",
      }));
    }
  }

  // Handle Team Lead Select Change
  function handleLeadSelect(val) {
    const lead = availableTeamLeads.find((u) => (u.uid || u.id || u.displayName) === val);
    if (lead) {
      setScheduleData((prev) => ({
        ...prev,
        interviewerId: lead.uid || lead.id || "",
        interviewerName: lead.displayName || lead.name || val,
        interviewerDept: lead.department || "",
      }));
    } else {
      setScheduleData((prev) => ({
        ...prev,
        interviewerId: "",
        interviewerName: val,
        interviewerDept: "",
      }));
    }
  }

  // Validation & Touch State
  const [touchedFields, setTouchedFields] = useState({});
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleBlur = (field) => {
    setTouchedFields((prev) => ({ ...prev, [field]: true }));
  };

  const isOffline = scheduleData.meetingMode === "In Person" || scheduleData.meetingMode === "Offline";

  // Form Validation Logic for all mandatory (*) fields
  const formErrors = useMemo(() => {
    const errs = {};

    if (!scheduleData.candidateId) {
      errs.candidateId = "Candidate selection is required";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!scheduleData.candidateEmail || !emailRegex.test(scheduleData.candidateEmail)) {
      errs.candidateEmail = "Valid candidate email is required";
    }

    if (!scheduleData.round) {
      errs.round = "Interview round is required";
    }

    if (!scheduleData.interviewerId && !scheduleData.interviewerName) {
      errs.interviewer = "Interviewer (Team Lead) selection is required";
    }

    if (!scheduleData.scheduledDate) {
      errs.scheduledDate = "Date is required";
    }

    if (!scheduleData.scheduledTime) {
      errs.scheduledTime = "Time is required";
    }

    if (!scheduleData.meetingMode) {
      errs.meetingMode = "Meeting mode is required";
    }

    if (!scheduleData.meetingLink || scheduleData.meetingLink.trim() === "") {
      errs.meetingLink = isOffline
        ? "Physical location is required for in-person/offline interviews"
        : "Meeting link URL is required for online interviews";
    }

    return errs;
  }, [scheduleData, isOffline]);

  const isFormValid = Object.keys(formErrors).length === 0;

  const showErrorFor = (field) => (attemptedSubmit || touchedFields[field]) && formErrors[field];

  // Submit Schedule Interview Form
  async function handleScheduleSubmit(e) {
    e.preventDefault();
    setAttemptedSubmit(true);

    if (!isFormValid) {
      toast.error("Please complete all required fields before scheduling the interview.");
      return;
    }

    setSubmitting(true);

    try {
      await createInterview({
        candidateId: scheduleData.candidateId,
        candidateEmail: scheduleData.candidateEmail,
        candidateName: scheduleData.candidateName,
        jobTitle: scheduleData.jobTitle,
        round: scheduleData.round,
        interviewerId: scheduleData.interviewerId,
        interviewerName: scheduleData.interviewerName,
        interviewerDept: scheduleData.interviewerDept,
        assignedLeadId: scheduleData.interviewerId,
        assignedLeadName: scheduleData.interviewerName,
        scheduledDate: scheduleData.scheduledDate,
        scheduledTime: scheduleData.scheduledTime,
        duration: scheduleData.duration,
        meetingMode: scheduleData.meetingMode,
        meetingLink: scheduleData.meetingLink,
        notes: scheduleData.notes,
        sendEmailNotification: scheduleData.sendEmailNotification,
        createdByName: userProfile?.displayName || userProfile?.name || "HR Admin",
      });

      toast.success(
        `✅ Interview scheduled & invitation email sent to ${scheduleData.candidateEmail}! ${scheduleData.candidateName} assigned to ${scheduleData.interviewerName}.`,
        { duration: 5000 }
      );

      // Reset Form & Validation State
      setScheduleData({
        candidateId: "",
        candidateEmail: "",
        candidateName: "",
        jobTitle: "",
        round: "Technical Round 1",
        interviewerId: "",
        interviewerName: "",
        interviewerDept: "",
        scheduledDate: new Date().toISOString().split("T")[0],
        scheduledTime: "14:00",
        duration: "45 mins",
        meetingMode: "Google Meet",
        meetingLink: "",
        notes: "",
        sendEmailNotification: true,
      });
      setTouchedFields({});
      setAttemptedSubmit(false);
    } catch (err) {
      toast.error("Failed to schedule interview: " + err.message);
    } finally {
      setSubmitting(false);
    }
  }

  // Handle Cancel
  function handleCancel() {
    setScheduleData({
      candidateId: "",
      candidateEmail: "",
      candidateName: "",
      jobTitle: "",
      round: "Technical Round 1",
      interviewerId: "",
      interviewerName: "",
      interviewerDept: "",
      scheduledDate: new Date().toISOString().split("T")[0],
      scheduledTime: "14:00",
      duration: "45 mins",
      meetingMode: "Google Meet",
      meetingLink: "",
      notes: "",
      sendEmailNotification: true,
    });
    setTouchedFields({});
    setAttemptedSubmit(false);
    toast("Form reset", { icon: "🧹" });
  }

  // Technical Feedback Submission
  async function handleFeedbackSubmit(e) {
    e.preventDefault();
    if (!selectedInterview) return;
    try {
      await submitInterviewFeedback(selectedInterview.id, feedbackData, selectedInterview.candidateId);
      setShowFeedbackModal(false);
      toast.success("Technical evaluation feedback submitted successfully!");
    } catch (err) {
      toast.error("Failed to submit feedback: " + err.message);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      <style>{`
        .scheduler-main-grid {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 460px;
          gap: 1.25rem;
          align-items: start;
        }
        @media (max-width: 1120px) {
          .scheduler-main-grid {
            grid-template-columns: 1fr;
          }
        }

        .int-table-wrapper {
          width: 100%;
          overflow-x: auto;
          border-radius: 12px;
          border: 1px solid var(--border, #e5e7eb);
          background: var(--bg-card, #ffffff);
          box-shadow: 0 1px 5px rgba(0,0,0,0.05);
          -webkit-overflow-scrolling: touch;
        }
        .int-table {
          width: 100%;
          min-width: 840px;
          border-collapse: collapse;
          font-size: 0.84rem;
          table-layout: auto;
        }
        .int-table th {
          background: var(--bg-secondary, #f9fafb);
          text-align: left;
          padding: 0.8rem 0.85rem;
          font-size: 0.74rem;
          font-weight: 700;
          color: var(--text-muted, #64748b);
          text-transform: uppercase;
          letter-spacing: 0.04em;
          border-bottom: 2px solid var(--border, #e5e7eb);
          white-space: nowrap;
        }
        .int-table td {
          padding: 0.85rem;
          border-bottom: 1px solid var(--border, #f3f4f6);
          vertical-align: middle;
          color: var(--text-primary, #111);
        }
        .int-table th.th-action, .int-table td.col-action-cell {
          text-align: center;
        }

        .btn-cancel-interview {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.35rem;
          background: #fffbeb;
          color: #b45309;
          border: 1.5px solid #fcd34d;
          padding: 0.38rem 0.75rem;
          border-radius: 8px;
          font-weight: 700;
          font-size: 0.76rem;
          cursor: pointer;
          transition: all 0.18s ease;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .btn-cancel-interview:hover {
          background: #fef3c7;
          border-color: #f59e0b;
          color: #92400e;
          box-shadow: 0 2px 6px rgba(245,158,11,0.2);
        }

        .btn-delete-interview {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.35rem;
          background: #ffffff;
          color: #dc2626;
          border: 1.5px solid #fca5a5;
          padding: 0.38rem 0.75rem;
          border-radius: 8px;
          font-weight: 700;
          font-size: 0.76rem;
          cursor: pointer;
          transition: all 0.18s ease;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .btn-delete-interview:hover {
          background: #fef2f2;
          border-color: #ef4444;
          color: #b91c1c;
          box-shadow: 0 2px 8px rgba(239,68,68,0.15);
        }

        .scheduler-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 1.6rem;
          box-shadow: 0 10px 25px -5px rgba(15, 23, 42, 0.05), 0 8px 10px -6px rgba(15, 23, 42, 0.03);
          display: flex;
          flex-direction: column;
          gap: 1.2rem;
        }

        .ats-input, .ats-select {
          width: 100%;
          height: 42px;
          padding: 0 0.85rem;
          border: 1.5px solid #cbd5e1;
          border-radius: 10px;
          font-size: 0.85rem;
          font-weight: 500;
          color: #0f172a;
          background-color: #ffffff;
          transition: all 0.2s ease-in-out;
          box-sizing: border-box;
          outline: none;
          font-family: inherit;
        }

        .ats-input:focus, .ats-select:focus {
          border-color: #1b4b31;
          box-shadow: 0 0 0 3.5px rgba(27, 75, 49, 0.12);
          background-color: #ffffff;
        }

        .ats-input-error, .ats-select-error {
          border-color: #ef4444 !important;
          background-color: #fef2f2 !important;
        }
        .ats-input-error:focus, .ats-select-error:focus {
          border-color: #dc2626 !important;
          box-shadow: 0 0 0 3.5px rgba(239, 68, 68, 0.2) !important;
        }

        .field-error-msg {
          font-size: 0.73rem;
          font-weight: 600;
          color: #dc2626;
          margin-top: 0.25rem;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .ats-input:read-only, .ats-input:disabled {
          background-color: #f8fafc;
          border-color: #e2e8f0;
          color: #64748b;
          cursor: not-allowed;
        }

        textarea.ats-input {
          height: auto;
          padding: 0.65rem 0.85rem;
        }

        .ats-label {
          display: flex;
          align-items: center;
          gap: 0.35rem;
          font-size: 0.8rem;
          font-weight: 700;
          color: #334155;
          margin-bottom: 0.25rem;
        }

        .summary-card {
          background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
          border: 1.5px solid #a7f3d0;
          border-radius: 12px;
          padding: 1rem 1.15rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          box-shadow: 0 2px 6px rgba(16, 185, 129, 0.06);
        }

        .sched-btn-primary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          background: linear-gradient(135deg, #1b4b31 0%, #065f46 100%);
          color: #ffffff;
          border: none;
          padding: 0.65rem 1.35rem;
          border-radius: 10px;
          font-weight: 700;
          font-size: 0.86rem;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(27, 75, 49, 0.25);
          transition: all 0.2s ease-in-out;
        }

        .sched-btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #143724 0%, #044e39 100%);
          box-shadow: 0 6px 16px rgba(27, 75, 49, 0.35);
          transform: translateY(-1px);
        }

        .sched-btn-primary:disabled {
          background: #94a3b8 !important;
          cursor: not-allowed !important;
          box-shadow: none !important;
          opacity: 0.75;
        }

        .sched-btn-secondary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          background: #ffffff;
          color: #475569;
          border: 1.5px solid #cbd5e1;
          padding: 0.65rem 1.2rem;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.86rem;
          cursor: pointer;
          transition: all 0.2s ease-in-out;
        }

        .sched-btn-secondary:hover {
          background: #f8fafc;
          border-color: #94a3b8;
          color: #0f172a;
        }

        .form-grid-2col {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }
        @media (max-width: 640px) {
          .form-grid-2col { grid-template-columns: 1fr; }
        }
      `}</style>

      {/* Main Two-Column Layout */}
      <div className="scheduler-main-grid">
        
        {/* Left Side: Scheduled Interviews List */}
        <div style={{ display: "grid", gap: "1rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <h3 style={{ margin: 0, fontWeight: 700, fontSize: "1.05rem", color: "#0f172a" }}>Scheduled Interviews</h3>
              <span style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>{interviews.length} active & completed interview rounds</span>
            </div>
          </div>

          {interviewsLoading ? (
            <div style={{ background: "var(--bg-card, #fff)", border: "1px solid var(--border, #e5e7eb)", borderRadius: 16, padding: "2rem", textAlign: "center", color: "var(--text-muted)" }}>
              Loading Scheduled Interviews...
            </div>
          ) : interviews.length === 0 ? (
            <div style={{ background: "var(--bg-card, #fff)", border: "1px dashed var(--border)", borderRadius: 16, padding: "2.5rem", textAlign: "center", color: "var(--text-muted)" }}>
              No interviews scheduled yet.
              <div style={{ marginTop: "0.5rem", fontSize: "0.82rem" }}>HR can use the form on the right to schedule candidate rounds.</div>
            </div>
          ) : (
            <div className="int-table-wrapper">
              <table className="int-table">
                <thead>
                  <tr>
                    <th style={{ minWidth: 160 }}>Candidate</th>
                    <th style={{ minWidth: 130 }}>Round &amp; Mode</th>
                    <th style={{ minWidth: 140 }}>Interviewer</th>
                    <th style={{ minWidth: 135 }}>Date &amp; Time</th>
                    <th style={{ minWidth: 160 }}>Meeting Link / Location</th>
                    <th className="th-action" style={{ minWidth: 180 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {interviews.map((item) => (
                    <tr key={item.id}>
                      <td style={{ minWidth: 160 }}>
                        <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.85rem" }}>{item.candidateName}</div>
                        <div style={{ fontSize: "0.74rem", color: "var(--text-muted)", marginTop: 2 }}>{item.candidateEmail || item.jobTitle || "Applicant"}</div>
                      </td>
                      <td style={{ minWidth: 130 }}>
                        <span style={{ background: "#eaf4ee", color: "#1b4b31", padding: "0.25rem 0.6rem", borderRadius: 6, fontSize: "0.74rem", fontWeight: 700, display: "inline-block", whiteSpace: "nowrap", border: "1px solid #a7f3d0" }}>
                          {item.round || "Technical Round"}
                        </span>
                      </td>
                      <td style={{ minWidth: 140 }}>
                        <div style={{ fontWeight: 600, fontSize: "0.83rem", color: "#1e293b" }}>{item.interviewerName || "Unassigned"}</div>
                        <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: 2 }}>Team Lead Evaluator</div>
                      </td>
                      <td style={{ minWidth: 135 }}>
                        <div style={{ fontWeight: 600, fontSize: "0.82rem", color: "#0f172a", whiteSpace: "nowrap" }}>{item.scheduledDate}</div>
                        <div style={{ fontSize: "0.73rem", color: "var(--text-muted)", marginTop: 2, whiteSpace: "nowrap" }}>{item.scheduledTime} ({item.duration || "45m"})</div>
                      </td>
                      <td style={{ minWidth: 160 }}>
                        {item.meetingLink ? (
                          <a
                            href={item.meetingLink.startsWith("http") ? item.meetingLink : `https://${item.meetingLink}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              color: "#1b4b31",
                              fontWeight: 700,
                              fontSize: "0.78rem",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "0.35rem",
                              padding: "0.3rem 0.65rem",
                              background: "#eaf4ee",
                              border: "1px solid #a7f3d0",
                              borderRadius: "8px",
                              textDecoration: "none",
                              whiteSpace: "nowrap",
                              transition: "all 0.15s ease",
                            }}
                            title={item.meetingLink}
                          >
                            <Video size={14} style={{ flexShrink: 0, color: "#1b4b31" }} />
                            <span>{item.meetingMode || "Google Meet"}</span>
                          </a>
                        ) : (
                          <span style={{ fontSize: "0.75rem", color: "var(--text-muted)", display: "inline-flex", alignItems: "center", gap: "0.3rem", whiteSpace: "nowrap" }}>
                            <MapPin size={13} style={{ flexShrink: 0 }} /> {item.meetingMode || "In Person"}
                          </span>
                        )}
                      </td>
                      <td className="col-action-cell" style={{ minWidth: 180 }}>
                        <div style={{ display: "flex", gap: "0.4rem", justifyContent: "center", alignItems: "center" }}>
                          <button
                            className="btn-cancel-interview"
                            onClick={() => handleCancelInterview(item)}
                            title="Cancel interview schedule"
                          >
                            <XCircle size={14} style={{ flexShrink: 0 }} /> Cancel
                          </button>
                          <button
                            className="btn-delete-interview"
                            onClick={async () => {
                              const confirm = window.confirm("Are you sure you want to delete this interview schedule?");
                              if (confirm) {
                                await handleConfirmDelete(item);
                              }
                            }}
                            title="Delete interview schedule"
                          >
                            <Trash2 size={14} style={{ flexShrink: 0 }} /> Delete
                          </button>
                        </div>
                      </td>

                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Right Side: Enterprise HR Interview Scheduling Panel */}
        <div className="scheduler-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #f1f5f9", paddingBottom: "0.85rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
              <div style={{ background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 10, width: 38, height: 38, display: "flex", alignItems: "center", justifyContent: "center", color: "#065f46", flexShrink: 0 }}>
                <Calendar size={20} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontWeight: 800, fontSize: "1.05rem", color: "#0f172a" }}>
                  HR Interview Scheduling Panel
                </h3>
                <p style={{ margin: "2px 0 0 0", fontSize: "0.78rem", color: "#64748b" }}>
                  Select candidate, pick Team Lead evaluator, & send invite
                </p>
              </div>
            </div>
            <span style={{ background: "#ecfdf5", color: "#065f46", border: "1px solid #a7f3d0", fontWeight: 700, fontSize: "0.72rem", padding: "0.25rem 0.65rem", borderRadius: 99 }}>
              Enterprise ATS
            </span>
          </div>

          {/* Compact Candidate Summary Card (Visible upon candidate selection) */}
          {selectedCandidateObj && (
            <div className="summary-card">
              <div>
                <div style={{ fontWeight: 800, fontSize: "0.92rem", color: "#065f46", display: "flex", alignItems: "center", gap: "0.4rem" }}>
                  <UserCheck size={16} /> {selectedCandidateObj.name}
                </div>
                <div style={{ fontSize: "0.78rem", color: "#166534", marginTop: 3 }}>
                  Position: <b>{selectedCandidateObj.appliedPosition || selectedCandidateObj.role || "Applicant"}</b> · Dept: {selectedCandidateObj.department || "Engineering"}
                </div>
                <div style={{ fontSize: "0.74rem", color: "#15803d", marginTop: 2, display: "flex", alignItems: "center", gap: "0.3rem" }}>
                  <Mail size={13} /> {scheduleData.candidateEmail}
                </div>
              </div>
              <span style={{ fontSize: "0.72rem", fontWeight: 700, background: "#10b981", color: "#fff", padding: "0.2rem 0.65rem", borderRadius: 99, flexShrink: 0 }}>
                Shortlisted
              </span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleScheduleSubmit} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            
            {/* Candidate Dropdown */}
            <div>
              <label className="ats-label">
                <User size={15} style={{ color: "#1b4b31" }} /> Candidate <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <select
                className={`ats-select ${showErrorFor("candidateId") ? "ats-select-error" : ""}`}
                value={scheduleData.candidateId}
                onBlur={() => handleBlur("candidateId")}
                onChange={(e) => {
                  handleCandidateSelect(e.target.value);
                  handleBlur("candidateId");
                }}
              >
                <option value="">-- Select Candidate --</option>
                {shortlistedCandidates.map((c) => {
                  const candidateName = c.name || c.displayName || "Candidate";
                  const appliedPosition = c.appliedPosition || c.jobTitle || c.role || "Applicant";
                  const currentStage = c.stage || c.status || "Applied";
                  return (
                    <option key={c.id} value={c.id}>
                      {`${candidateName} – ${appliedPosition} – ${currentStage}`}
                    </option>
                  );
                })}
              </select>
              {showErrorFor("candidateId") && (
                <div className="field-error-msg">⚠️ {formErrors.candidateId}</div>
              )}
            </div>

            {/* Candidate Email (Auto-filled for invitation) */}
            <div>
              <label className="ats-label">
                <Mail size={15} style={{ color: "#1b4b31" }} /> Candidate Email (Auto-filled for invitation) <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                type="email"
                className={`ats-input ${showErrorFor("candidateEmail") ? "ats-input-error" : ""}`}
                placeholder="Select candidate or enter email address..."
                value={scheduleData.candidateEmail}
                onBlur={() => handleBlur("candidateEmail")}
                onChange={(e) => {
                  setScheduleData({ ...scheduleData, candidateEmail: e.target.value });
                  handleBlur("candidateEmail");
                }}
              />
              {showErrorFor("candidateEmail") && (
                <div className="field-error-msg">⚠️ {formErrors.candidateEmail}</div>
              )}
            </div>

            {/* Round & Interviewer (Team Lead) Grid */}
            <div className="form-grid-2col">
              <div>
                <label className="ats-label">
                  <Briefcase size={15} style={{ color: "#1b4b31" }} /> Interview Round <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <select
                  className={`ats-select ${showErrorFor("round") ? "ats-select-error" : ""}`}
                  value={scheduleData.round}
                  onBlur={() => handleBlur("round")}
                  onChange={(e) => {
                    setScheduleData({ ...scheduleData, round: e.target.value });
                    handleBlur("round");
                  }}
                >
                  <option value="HR Round">HR Round</option>
                  <option value="Technical Round 1">Technical Round 1</option>
                  <option value="Technical Round 2">Technical Round 2</option>
                  <option value="Manager Round">Manager Round</option>
                  <option value="Final Round">Final Round</option>
                </select>
                {showErrorFor("round") && (
                  <div className="field-error-msg">⚠️ {formErrors.round}</div>
                )}
              </div>

              <div>
                <label className="ats-label">
                  <UserCheck size={15} style={{ color: "#1b4b31" }} /> Interviewer (Team Lead) <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <select
                  className={`ats-select ${showErrorFor("interviewer") ? "ats-select-error" : ""}`}
                  value={scheduleData.interviewerId || scheduleData.interviewerName}
                  onBlur={() => handleBlur("interviewer")}
                  onChange={(e) => {
                    handleLeadSelect(e.target.value);
                    handleBlur("interviewer");
                  }}
                >
                  <option value="">-- Select Team Lead --</option>
                  {availableTeamLeads.map((u) => (
                    <option key={u.uid || u.id || u.displayName} value={u.uid || u.id || u.displayName}>
                      {u.displayName || u.name} ({u.department || "Lead"})
                    </option>
                  ))}
                </select>
                {showErrorFor("interviewer") && (
                  <div className="field-error-msg">⚠️ {formErrors.interviewer}</div>
                )}
              </div>
            </div>

            {/* Date, Time & Duration Grid */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.75rem" }}>
              <div>
                <label className="ats-label">
                  <Calendar size={14} style={{ color: "#1b4b31" }} /> Date <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="date"
                  className={`ats-input ${showErrorFor("scheduledDate") ? "ats-input-error" : ""}`}
                  value={scheduleData.scheduledDate}
                  onBlur={() => handleBlur("scheduledDate")}
                  onChange={(e) => {
                    setScheduleData({ ...scheduleData, scheduledDate: e.target.value });
                    handleBlur("scheduledDate");
                  }}
                />
                {showErrorFor("scheduledDate") && (
                  <div className="field-error-msg">⚠️ {formErrors.scheduledDate}</div>
                )}
              </div>

              <div>
                <label className="ats-label">
                  <Clock size={14} style={{ color: "#1b4b31" }} /> Time <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="time"
                  className={`ats-input ${showErrorFor("scheduledTime") ? "ats-input-error" : ""}`}
                  value={scheduleData.scheduledTime}
                  onBlur={() => handleBlur("scheduledTime")}
                  onChange={(e) => {
                    setScheduleData({ ...scheduleData, scheduledTime: e.target.value });
                    handleBlur("scheduledTime");
                  }}
                />
                {showErrorFor("scheduledTime") && (
                  <div className="field-error-msg">⚠️ {formErrors.scheduledTime}</div>
                )}
              </div>

              <div>
                <label className="ats-label">Duration</label>
                <select
                  className="ats-select"
                  value={scheduleData.duration}
                  onChange={(e) => setScheduleData({ ...scheduleData, duration: e.target.value })}
                >
                  <option value="30 mins">30 mins</option>
                  <option value="45 mins">45 mins</option>
                  <option value="60 mins">60 mins</option>
                  <option value="90 mins">90 mins</option>
                </select>
              </div>
            </div>

            {/* Meeting Mode & Link/Location Grid */}
            <div className="form-grid-2col">
              <div>
                <label className="ats-label">
                  <Video size={15} style={{ color: "#1b4b31" }} /> Meeting Mode <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <select
                  className={`ats-select ${showErrorFor("meetingMode") ? "ats-select-error" : ""}`}
                  value={scheduleData.meetingMode}
                  onBlur={() => handleBlur("meetingMode")}
                  onChange={(e) => {
                    const mode = e.target.value;
                    setScheduleData({ ...scheduleData, meetingMode: mode });
                    handleBlur("meetingMode");
                  }}
                >
                  <option value="Google Meet">Google Meet</option>
                  <option value="Microsoft Teams">Microsoft Teams</option>
                  <option value="In Person">In Person</option>
                  <option value="Offline">Offline</option>
                </select>
                {showErrorFor("meetingMode") && (
                  <div className="field-error-msg">⚠️ {formErrors.meetingMode}</div>
                )}
              </div>

              <div>
                <label className="ats-label">
                  <MapPin size={15} style={{ color: "#1b4b31" }} /> {isOffline ? "Physical Location" : "Meeting Link / URL"} <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="text"
                  className={`ats-input ${showErrorFor("meetingLink") ? "ats-input-error" : ""}`}
                  placeholder={isOffline ? "Conference Room B, Floor 3 (Required)" : "https://meet.google.com/xyz-abc-123 (Required)"}
                  value={scheduleData.meetingLink}
                  onBlur={() => handleBlur("meetingLink")}
                  onChange={(e) => {
                    setScheduleData({ ...scheduleData, meetingLink: e.target.value });
                    handleBlur("meetingLink");
                  }}
                />
                {showErrorFor("meetingLink") && (
                  <div className="field-error-msg">⚠️ {formErrors.meetingLink}</div>
                )}
              </div>
            </div>

            {/* Special Instructions */}
            <div>
              <label className="ats-label">Special Instructions / Interview Topics</label>
              <textarea
                className="ats-input"
                style={{ height: 60, resize: "vertical" }}
                placeholder="Topics: System Design, Live Coding, Portfolio Review..."
                value={scheduleData.notes}
                onChange={(e) => setScheduleData({ ...scheduleData, notes: e.target.value })}
              />
            </div>

            {/* Send Email Notification Checkbox */}
            <div style={{ display: "flex", alignItems: "center", gap: "0.55rem", marginTop: "0.2rem" }}>
              <input
                type="checkbox"
                id="sendEmailCheck"
                style={{ width: 17, height: 17, accentColor: "#1b4b31", cursor: "pointer" }}
                checked={scheduleData.sendEmailNotification}
                onChange={(e) => setScheduleData({ ...scheduleData, sendEmailNotification: e.target.checked })}
              />
              <label htmlFor="sendEmailCheck" style={{ fontSize: "0.82rem", fontWeight: 600, color: "#334155", cursor: "pointer" }}>
                Send Email Notification to candidate & interviewer
              </label>
            </div>

            {/* Action Buttons: Cancel and Schedule Interview */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "0.6rem", borderTop: "1px solid #f1f5f9", paddingTop: "1rem" }}>
              <button type="button" className="sched-btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
              <div
                onClick={() => {
                  if (!isFormValid || submitting) {
                    setAttemptedSubmit(true);
                    toast.error("Please complete all required fields before scheduling the interview.");
                  }
                }}
                style={{ display: "inline-block" }}
              >
                <button
                  type="submit"
                  className="sched-btn-primary"
                  disabled={!isFormValid || submitting}
                  style={{
                    pointerEvents: isFormValid && !submitting ? "auto" : "none",
                  }}
                >
                  <Calendar size={16} /> {submitting ? "Scheduling..." : "Schedule Interview"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Technical Evaluation Feedback Modal */}
      {showFeedbackModal && (
        <div className="modal-overlay">
          <div className="modal-body" style={{ maxWidth: 520, background: "#fff", borderRadius: 16, padding: "1.75rem" }}>
            <h3 style={{ margin: "0 0 0.25rem", fontWeight: 800, fontSize: "1.1rem" }}>Team Lead Technical Evaluation</h3>
            <p style={{ fontSize: "0.78rem", color: "var(--text-muted)", margin: "0 0 1rem" }}>
              Candidate: <b>{selectedInterview?.candidateName}</b> · Round: {selectedInterview?.round}
            </p>

            <form onSubmit={handleFeedbackSubmit} style={{ display: "flex", flexDirection: "column", gap: "0.95rem" }}>
              <div>
                <label style={{ fontSize: "0.8rem", fontWeight: 700, color: "#334155" }}>Overall Score (Out of 10) *</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  step="0.5"
                  className="ats-input"
                  style={{ width: "100%" }}
                  required
                  value={feedbackData.rating}
                  onChange={(e) => setFeedbackData({ ...feedbackData, rating: Number(e.target.value) })}
                />
              </div>

              <div>
                <label style={{ fontSize: "0.8rem", fontWeight: 700, color: "#334155" }}>Recommendation *</label>
                <select
                  className="ats-select"
                  style={{ width: "100%" }}
                  required
                  value={feedbackData.recommendation}
                  onChange={(e) => setFeedbackData({ ...feedbackData, recommendation: e.target.value })}
                >
                  <option value="Hire">✔ Recommend Hire</option>
                  <option value="Hold">⏳ Hold / Secondary Evaluation Required</option>
                  <option value="Reject">❌ Reject Candidate</option>
                </select>
              </div>

              <div>
                <label style={{ fontSize: "0.8rem", fontWeight: 700, color: "#334155" }}>Technical Assessment Feedback</label>
                <textarea
                  className="ats-input"
                  style={{ width: "100%", height: 95, resize: "vertical" }}
                  required
                  placeholder="Evaluate coding ability, domain knowledge, problem solving, strengths and weaknesses..."
                  value={feedbackData.notes}
                  onChange={(e) => setFeedbackData({ ...feedbackData, notes: e.target.value })}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.5rem" }}>
                <button type="button" className="btn-secondary" onClick={() => setShowFeedbackModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Save Technical Evaluation</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
